import type { PlanConfig } from './types.ts';

/** Structural checks for a plan config (ADR-0020). Run at build time and when a plan is loaded on a device. */
export function validatePlan(p: PlanConfig): string[] {
  const errs: string[] = [];
  const need = (ok: unknown, msg: string) => void (!ok && errs.push(msg));
  need(p && p.schema === 1, 'schema must be 1');
  need(typeof p.id === 'string' && /^[a-z0-9-]+$/.test(p.id), 'id must be kebab-case');
  need(Number.isInteger(p.version) && p.version >= 1, 'version must be a positive integer');
  need(typeof p.title === 'string' && p.title.length > 0 && p.title.length <= 60, 'title is required (≤ 60 chars)');
  need(Number.isInteger(p.daysPerWeek) && p.daysPerWeek >= 1 && p.daysPerWeek <= 7, 'daysPerWeek must be 1–7');
  need(Array.isArray(p.rotation) && p.rotation.length > 0, 'rotation must list workouts');
  need(p.apply === 'afterTest' || p.apply === 'weekEnd', 'apply must be afterTest or weekEnd');
  need(Array.isArray(p.deviations), 'deviations must be an array (empty if none)');
  const trackIds = new Set<string>();
  for (const t of p.tracks ?? []) {
    need(!trackIds.has(t.id), `duplicate track ${t.id}`);
    trackIds.add(t.id);
    need(t.increment && t.increment.kg > 0 && t.increment.lb > 0, `track ${t.id}: increment must be positive in kg and lb`);
    need(t.increment.kg <= 10 && t.increment.lb <= 20, `track ${t.id}: increment too large`);
    need(['fixed', 'e1rm'].includes(t.start?.type), `track ${t.id}: unknown start rule`);
    need(['allReps', 'minReps', 'targetReps', 'always'].includes(t.progress?.pass?.type), `track ${t.id}: unknown pass rule`);
    if (t.rampStep !== undefined) need(t.rampStep > 0 && t.rampStep <= 0.25, `track ${t.id}: rampStep must be 0–25%`);
    if (t.inputFrom) need(trackIds.has(t.inputFrom.track) || (p.tracks ?? []).some((x) => x.id === t.inputFrom!.track), `track ${t.id}: inputFrom refers to an unknown track`);
  }
  const workoutIds = new Set((p.workouts ?? []).map((w) => w.id));
  for (const r of p.rotation ?? []) need(workoutIds.has(r), `rotation names unknown workout ${r}`);
  for (const w of p.workouts ?? [])
    for (const e of w.exercises) {
      need(trackIds.has(e.track), `workout ${w.id}: unknown track ${e.track}`);
      need(e.sets.length > 0 && e.sets.length <= 12, `workout ${w.id} ${e.track}: 1–12 sets`);
      for (const s of e.sets) {
        need(Number.isInteger(s.reps) && s.reps >= 1 && s.reps <= 30, `workout ${w.id} ${e.track}: reps 1–30`);
        if (s.pct !== undefined) need(s.pct > 0 && s.pct <= 1.5, `workout ${w.id} ${e.track}: pct out of range`);
        if (s.step !== undefined) need(Number.isInteger(s.step) && s.step >= 0 && s.step <= 8, `workout ${w.id} ${e.track}: step 0–8`);
      }
    }
  for (const t of p.tracks ?? []) {
    const tw = t.progress?.test?.workout;
    if (tw) need(workoutIds.has(tw), `track ${t.id}: test workout ${tw} is unknown`);
  }
  for (const va of p.variations ?? []) {
    need(typeof va.id === 'string' && va.id.length > 0 && typeof va.label === 'string', 'variation needs an id and a label');
    for (const [tid, x] of Object.entries(va.tracks ?? {})) {
      const t = p.tracks.find((y) => y.id === tid);
      need(!!t, `variation ${va.id}: unknown track ${tid}`);
      need(!!t?.inputFrom, `variation ${va.id}: track ${tid} must derive from another track`);
      need(x.pct > 0 && x.pct <= 2 && Number.isInteger(x.reps) && x.reps > 0, `variation ${va.id}: ${tid} needs pct and reps`);
    }
  }
  return errs;
}

export interface ExerciseCatalogue {
  schema: 1;
  families: Record<string, string>;
  exercises: { id: string; name: string; family: string; equipment: string[]; bodyweight?: boolean; perHand?: boolean; timed?: boolean }[];
}

export function validateExercises(c: ExerciseCatalogue): string[] {
  const errs: string[] = [];
  const seen = new Set<string>();
  for (const e of c.exercises ?? []) {
    if (seen.has(e.id)) errs.push(`duplicate exercise ${e.id}`);
    seen.add(e.id);
    if (!c.families[e.family]) errs.push(`${e.id}: unknown family ${e.family}`);
    if (!e.name) errs.push(`${e.id}: name required`);
  }
  return errs;
}

/** Every exercise a plan uses must be in the catalogue (so names, families and equipment resolve offline). */
export function planExercisesKnown(p: PlanConfig, c: ExerciseCatalogue): string[] {
  const ids = new Set(c.exercises.map((e) => e.id));
  const used = [...p.tracks.map((t) => t.exercise), ...(p.variations ?? []).flatMap((va) => Object.values(va.tracks).map((x) => x.exercise))];
  return [...new Set(used)].filter((e) => !ids.has(e)).map((e) => `${p.id}: exercise ${e} is not in the catalogue`);
}
